import { formatIST, parseIST } from '@/lib/time'

/**
 * Pulse v1 time-filter contract (oms-po-ingest/pulse FilterBar + Pos.tsx).
 *
 * Default is "All loaded" — the server applies a rolling 180-day floor on PO
 * date (`orderedAt` / Vision `external_po_date`), not an unbounded dump.
 * Presets 7d / 30d / 90d send only `from`. Custom is a closed range that
 * cannot exceed 180 days (6 × 30), matching v1's window math.
 */

export const MAX_RANGE_DAYS = 180

export type DatePreset = 'all' | '7d' | '30d' | '90d' | 'custom'

export interface DateFilter {
  preset: DatePreset
  from?: string
  to?: string
}

export const DEFAULT_DATE_FILTER: DateFilter = { preset: 'all' }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export function todayIso(now = new Date()): string {
  return formatIST(now, 'yyyy-MM-dd')
}

export function shiftIsoDate(now: Date, days: number): string {
  return formatIST(new Date(now.getTime() + days * 86_400_000), 'yyyy-MM-dd')
}

export function parseIsoDate(value: string): Date | null {
  if (!ISO_DATE.test(value)) return null
  const parsed = parseIST(`${value}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function daysBetween(from: string, to: string): number | null {
  const start = parseIsoDate(from)
  const end = parseIsoDate(to)
  if (!start || !end) return null
  return Math.round((end.getTime() - start.getTime()) / 86_400_000)
}

export function addDaysIso(iso: string, days: number): string {
  const parsed = parseIsoDate(iso)
  if (!parsed) return iso
  return formatIST(new Date(parsed.getTime() + days * 86_400_000), 'yyyy-MM-dd')
}

export function rangeWithinMax(from?: string, to?: string): boolean {
  if (!from || !to) return true
  const days = daysBetween(from, to)
  return days != null && days >= 0 && days <= MAX_RANGE_DAYS
}

export function clampCustomRange(from?: string, to?: string): { from?: string; to?: string } {
  if (!from || !to) return { from, to }
  const days = daysBetween(from, to)
  if (days == null) return { from, to }
  if (days < 0) return { from, to: from }
  if (days > MAX_RANGE_DAYS) return { from, to: addDaysIso(from, MAX_RANGE_DAYS) }
  return { from, to }
}

/** Resolve a filter to Vision `from` / `to` query params (YYYY-MM-DD). */
export function dateBounds(filter: DateFilter, now = new Date()): { from?: string; to?: string } {
  if (filter.preset === 'all') return {}
  if (filter.preset === 'custom') {
    const clamped = clampCustomRange(filter.from, filter.to)
    return {
      from: clamped.from || undefined,
      to: clamped.to || undefined,
    }
  }
  const days = filter.preset === '7d' ? 7 : filter.preset === '30d' ? 30 : 90
  return { from: shiftIsoDate(now, -days) }
}

export function parseDateFilter(params: URLSearchParams): DateFilter {
  const range = params.get('range')
  const from = params.get('from') ?? undefined
  const to = params.get('to') ?? undefined
  if (range === '7d' || range === '30d' || range === '90d' || range === 'all') {
    return { preset: range }
  }
  if (range === 'custom' || from || to) {
    const clamped = clampCustomRange(from, to)
    return { preset: 'custom', from: clamped.from, to: clamped.to }
  }
  return { ...DEFAULT_DATE_FILTER }
}

export function dateFilterSearchParams(filter: DateFilter): {
  range: string | null
  from: string | null
  to: string | null
} {
  if (filter.preset === 'all') {
    return { range: null, from: null, to: null }
  }
  if (filter.preset === 'custom') {
    const clamped = clampCustomRange(filter.from, filter.to)
    return {
      range: 'custom',
      from: clamped.from || null,
      to: clamped.to || null,
    }
  }
  return { range: filter.preset, from: null, to: null }
}
